import { useState, type CSSProperties, type ReactNode } from "react";
import type { TypeEntite } from "@hopitech/contracts";
import { inscriptionPublique } from "../lib/api.ts";
import { EDITIONS } from "../app/config.ts";
import { Lien } from "../lib/router.tsx";
import { ENTITES } from "../admin/entites.tsx";
import { FormulaireAdmin } from "../admin/ui.tsx";

/** Espace PUBLIC (hors connexion) : pré-inscriptions en libre-service. */

const EDITION = EDITIONS[0]!; // édition courante

// Profils ouverts au public → segment de config d'entité, libellé, intro.
const OFFRES: { type: TypeEntite; segment: string; titre: string; verbe: string; intro: string }[] = [
  { type: "exposant", segment: "exposants", titre: "Exposer", verbe: "Devenir exposant", intro: "Réservez votre présence sur le salon. Le bureau vous recontacte pour finaliser stand et facturation." },
  { type: "stagiaire", segment: "stagiaires", titre: "Se former", verbe: "M'inscrire à la formation", intro: "Inscrivez-vous à la formation, rattaché à votre établissement de santé." },
  { type: "visiteur", segment: "visiteurs", titre: "Visiter", verbe: "Préinscription visiteur", intro: "Préinscrivez-vous pour accélérer le retrait de votre badge à l'accueil." },
  { type: "intervenant", segment: "intervenants", titre: "Intervenir", verbe: "Proposer une intervention", intro: "Vous souhaitez intervenir ? Envoyez votre proposition ; le comité l'étudie." },
];

function PublicShell({ children }: { children: ReactNode }): React.ReactElement {
  const vars = { "--accent": EDITION.theme.accent, "--soft": EDITION.theme.soft } as CSSProperties;
  return (
    <div style={{ ...vars, minHeight: "100vh", background: "var(--paper)" }}>
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, padding: "14px 22px" }}>
          <Lien vers="/login" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <span className="display" style={{ width: 32, height: 32, borderRadius: 9, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>H</span>
            <span className="display" style={{ fontSize: 17, color: "var(--accent)" }}>Hopitech</span>
          </Lien>
          <span style={{ fontSize: 12.5, color: "var(--muted-2)" }}>{EDITION.optionLabel} · {EDITION.ville}</span>
          <span style={{ flex: 1 }} />
          <Lien vers="/login" className="btn btn-ghost" style={{ textDecoration: "none" }}>J'ai déjà un compte</Lien>
        </div>
      </header>
      <main className="hopi-in" style={{ maxWidth: 900, margin: "0 auto", padding: "34px 22px 80px" }}>{children}</main>
    </div>
  );
}

function PublicForm({ type }: { type: TypeEntite }): React.ReactElement {
  const offre = OFFRES.find((o) => o.type === type);
  const [fait, setFait] = useState(false);
  if (!offre) return <PublicShell><p>Parcours inconnu. <Lien vers="/login">← Retour</Lien></p></PublicShell>;
  const config = ENTITES[offre.segment];
  const champs = (config?.champs ?? []).filter((c) => (c.nom ?? c.label) !== "emplacement");

  if (fait) {
    return (
      <PublicShell>
        <div className="card" style={{ padding: "40px 28px", maxWidth: 520, margin: "20px auto", textAlign: "center", display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          <span style={{ width: 48, height: 48, borderRadius: 14, background: "var(--ok-bg)", color: "var(--ok)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800 }}>✓</span>
          <h2 className="display" style={{ fontSize: 22, margin: 0 }}>Pré-inscription enregistrée</h2>
          <p style={{ color: "var(--muted)", margin: 0 }}>Merci ! Le bureau Hopitech traite votre demande et vous recontacte. Un badge se retire ensuite à l'accueil.</p>
          <Lien vers="/login" className="btn btn-primary" style={{ textDecoration: "none" }}>Retour à l'accueil</Lien>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Lien vers="/login" style={{ fontSize: 13, color: "var(--muted)" }}>← Connexion</Lien>
      <h1 className="display" style={{ fontSize: 26, margin: "8px 0 4px" }}>{offre.verbe}</h1>
      <p style={{ color: "var(--muted)", margin: "0 0 18px", maxWidth: "62ch" }}>{offre.intro}</p>
      <FormulaireAdmin
        submit="Envoyer ma pré-inscription"
        blocs={[{ titre: "Vos informations", finder: config?.finder, champs }]}
        onSubmit={async (valeurs) => { await inscriptionPublique(type, EDITION.id, valeurs); setFait(true); }}
      />
    </PublicShell>
  );
}

export function PublicRoute({ chemin }: { chemin: string }): React.ReactElement {
  if (chemin.startsWith("/public/inscription/")) {
    return <PublicForm type={chemin.slice("/public/inscription/".length) as TypeEntite} />;
  }
  return <PublicShell><p>Parcours inconnu. <Lien vers="/login">Connexion</Lien></p></PublicShell>;
}
